import { CommonUI } from "@src/common/CommonUI/CommonUI";
import { IllustrationBG } from "@src/common/IllustrationBG/IllustrationBG";
import { InvocaPhoneNumber } from "@src/common/InvocaPhoneNumber";
import { PageLayout } from "@src/common/PageLayout";
import {
  APP_ROUTES,
  LAYOUT_CONSTANTS,
  LOCAL_STORAGE_KEYS,
  PHONE_NUMBER,
  SESSION_STORAGE_KEYS,
} from "@src/constants";
import { MEDIA_QUERIES } from "@src/constants/media-queries";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useTimerContext } from "@src/context/TimerContext";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import { useThemePageText } from "@src/hooks/useThemePageText";
import { renderPhoneTemplateText } from "@src/utils/render-phone-template-text";
import { useMediaQuery } from "@uhg-abyss/web/hooks/useMediaQuery";
import { Text } from "@uhg-abyss/web/ui/Text";
import { Fragment, useEffect } from "react";
import { AppointmentConfirmedCard } from "./AppointmentConfirmedCard";
import { PortalRegistrationCard } from "./PortalRegistrationCard";

export const AppointmentConfirmed = () => {
  const isMobile = useMediaQuery(MEDIA_QUERIES.mobileMax);
  const { isTimerRunning, stopTimer } = useTimerContext();
  const {
    data,
    setData,
    clearDataExcluding,
    clearAdditionalSavedModalData,
    clearLeadScopedStorage,
  } = useApplicationContext();
  const getThemePageText = useThemePageText();
  const isReferrerFlow = data?.isReferrerFlow === true;
  const labels = useResolvedPageLabels("appointmentConfirmed");
  const supportPhoneText = getThemePageText(
    "appointmentConfirmed",
    "supportPhoneText",
    labels.supportPhoneText,
  );

  useEffect(() => {
    sessionStorage.setItem(SESSION_STORAGE_KEYS.CONFIRMED_BACK, "true");
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.CONFIRMED_BACK_ROUTE,
      isReferrerFlow ? APP_ROUTES.REFER : APP_ROUTES.GET_STARTED,
    );
    clearDataExcluding([
      "appointmentDetails",
      "selectedProvider",
      "isReferrerFlow",
      "userRole",
      "userUuid",
      "ageRange",
      "entryRouteIdentityPath",
      "salesforceLeadId",
    ]); // Keep flow source + ageRange so confirmation copy stays in the correct (patient/self) voice
    clearAdditionalSavedModalData(); // Clear any additional saved modal data in localStorage to prevent stale data issues in future modals
    if (data.salesforceLeadId) {
      clearLeadScopedStorage(); // Evict lead-scoped localStorage to prevent quota exhaustion across multiple PAC sessions
    }
    setData(LOCAL_STORAGE_KEYS.COMPLETION_STATUS, true);
  }, []);

  useEffect(() => {
    // Stop the timer when this component mounts
    if (isTimerRunning) {
      stopTimer();
    }
  }, [isTimerRunning, stopTimer]);

  return (
    <PageLayout css={{ paddingBottom: isMobile ? "0" : "50px" }}>
      <IllustrationBG />
      <div
        data-testid="test-id-appointment-confirmed-content"
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "start",
        }}
      >
        <CommonUI
          headingText={labels.headingText}
          metaTitle={labels.headingText}
          bodyText={<Fragment>{labels.bodyText}</Fragment>}
          FormComponent={AppointmentConfirmedCard}
        />
        <PortalRegistrationCard />
        <div
          data-testid="test-id-appointment-confirmed-follow-up"
          style={{
            width: "100%",
            fontSize: isMobile ? "16px" : "18px",
            textAlign: "left",
            marginTop: isMobile ? "16px" : "20px",
            maxWidth: LAYOUT_CONSTANTS.maxFormWidth,
            display: "flex",
            flexDirection: "column",
            gap: isMobile ? "6px" : "8px",
            padding: isMobile ? "24px 16px" : "0",
          }}
        >
          <Text>{labels.followUpText}</Text>
          <Text>
            {renderPhoneTemplateText(supportPhoneText, PHONE_NUMBER, (phoneNumber) => (
              <span style={{ fontWeight: "700" }}>
                <InvocaPhoneNumber phoneNumber={phoneNumber} />
              </span>
            ))}
          </Text>
        </div>
      </div>
    </PageLayout>
  );
};
